import React, { useState, useEffect } from 'react';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { feedbackService, Feedback, FeedbackStatus } from '../../services/feedbackService';
import { Timestamp } from 'firebase/firestore';

const AdminFeedbackManager: React.FC = () => {
    const { isDark } = useAdminTheme();
    const { currentUser } = useAuth();
    const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
    const [responseText, setResponseText] = useState('');
    const [isResponding, setIsResponding] = useState(false);
    const [filterStatus, setFilterStatus] = useState<FeedbackStatus | 'all'>('all');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadFeedback();
    }, []);

    const loadFeedback = async () => {
        setIsLoading(true);
        try {
            const feedback = await feedbackService.getAllFeedback();
            setFeedbackList(feedback);
        } catch (error) {
            console.error('Error loading feedback:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStatusChange = async (feedbackId: string, newStatus: FeedbackStatus) => {
        try {
            await feedbackService.updateFeedbackStatus(feedbackId, newStatus);
            setFeedbackList(prev =>
                prev.map(f => f.id === feedbackId ? { ...f, status: newStatus } : f)
            );
            if (selectedFeedback?.id === feedbackId) {
                setSelectedFeedback(prev => prev ? { ...prev, status: newStatus } : null);
            }
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const handleRespond = async () => {
        if (!selectedFeedback || !responseText.trim() || !currentUser?.email) return;

        setIsResponding(true);
        try {
            await feedbackService.respondToFeedback(
                selectedFeedback.id,
                responseText,
                currentUser.email
            );

            // Update local state
            const updatedFeedback = {
                ...selectedFeedback,
                adminResponse: responseText,
                respondedBy: currentUser.email,
                respondedAt: Timestamp.now(),
                status: 'resolved' as FeedbackStatus
            };

            setFeedbackList(prev =>
                prev.map(f => f.id === selectedFeedback.id ? updatedFeedback : f)
            );
            setSelectedFeedback(updatedFeedback);
            setResponseText('');
        } catch (error) {
            console.error('Error responding to feedback:', error);
        } finally {
            setIsResponding(false);
        }
    };

    const handleDelete = async (feedbackId: string) => {
        if (!window.confirm('Are you sure you want to delete this feedback?')) return;

        try {
            await feedbackService.deleteFeedback(feedbackId);
            setFeedbackList(prev => prev.filter(f => f.id !== feedbackId));
            if (selectedFeedback?.id === feedbackId) {
                setSelectedFeedback(null);
            }
        } catch (error) {
            console.error('Error deleting feedback:', error);
        }
    };

    const formatDate = (timestamp: Timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate();
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getRatingEmoji = (rating: string | null) => {
        const ratings: Record<string, string> = {
            terrible: '😞',
            poor: '😕',
            average: '😐',
            good: '🙂',
            excellent: '😍'
        };
        return rating ? ratings[rating] || '❓' : '—';
    };

    const getStatusBadge = (status: FeedbackStatus) => {
        const styles = {
            new: isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800',
            read: isDark ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
            resolved: isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'
        };
        return (
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const getCategoryLabel = (category: string) => {
        const labels: Record<string, string> = {
            bug: '🐛 Bug Report',
            feature: '✨ Feature Request',
            general: '💬 General',
            ui: '🎨 UI/UX',
            other: '📝 Other'
        };
        return labels[category] || category;
    };

    const filteredFeedback = feedbackList.filter(f => {
        const matchesStatus = filterStatus === 'all' || f.status === filterStatus;
        const matchesSearch = !searchTerm ||
            f.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
            f.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
            f.userName.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesStatus && matchesSearch;
    });

    const counts = {
        all: feedbackList.length,
        new: feedbackList.filter(f => f.status === 'new').length,
        read: feedbackList.filter(f => f.status === 'read').length,
        resolved: feedbackList.filter(f => f.status === 'resolved').length
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header Stats */}
            <div className="grid grid-cols-4 gap-4">
                {[
                    { key: 'all', label: 'Total', count: counts.all, color: 'from-gray-500 to-gray-600' },
                    { key: 'new', label: 'New', count: counts.new, color: 'from-blue-500 to-blue-600' },
                    { key: 'read', label: 'Read', count: counts.read, color: 'from-yellow-500 to-yellow-600' },
                    { key: 'resolved', label: 'Resolved', count: counts.resolved, color: 'from-green-500 to-green-600' }
                ].map(stat => (
                    <button
                        key={stat.key}
                        onClick={() => setFilterStatus(stat.key as FeedbackStatus | 'all')}
                        className={`p-4 rounded-lg transition-all ${filterStatus === stat.key
                            ? `bg-gradient-to-r ${stat.color} text-white shadow-lg`
                            : isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50 shadow'
                            }`}
                    >
                        <p className={`text-2xl font-bold ${filterStatus !== stat.key && (isDark ? 'text-white' : 'text-gray-900')}`}>
                            {stat.count}
                        </p>
                        <p className={`text-sm ${filterStatus !== stat.key && (isDark ? 'text-gray-400' : 'text-gray-500')}`}>
                            {stat.label}
                        </p>
                    </button>
                ))}
            </div>

            {/* Search */}
            <div className={`rounded-lg shadow p-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search by email, name, or message..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`w-full pl-10 pr-4 py-2 rounded-lg border ${isDark
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                            } focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                    />
                </div>
            </div>

            <div className="flex gap-6">
                {/* Feedback List */}
                <div className={`flex-1 rounded-lg shadow overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                    <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Feedback ({filteredFeedback.length})
                        </h3>
                    </div>

                    {filteredFeedback.length === 0 ? (
                        <div className="p-8 text-center">
                            <svg className={`mx-auto h-12 w-12 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                            <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No feedback found</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
                            {filteredFeedback.map(feedback => (
                                <button
                                    key={feedback.id}
                                    onClick={() => {
                                        setSelectedFeedback(feedback);
                                        if (feedback.status === 'new') {
                                            handleStatusChange(feedback.id, 'read');
                                        }
                                    }}
                                    className={`w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${selectedFeedback?.id === feedback.id ? (isDark ? 'bg-gray-700' : 'bg-cyan-50') : ''
                                        }`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-lg">{getRatingEmoji(feedback.rating)}</span>
                                                <span className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                    {feedback.userEmail}
                                                </span>
                                                {getStatusBadge(feedback.status)}
                                            </div>
                                            <p className={`text-sm truncate ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                {feedback.message}
                                            </p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    {getCategoryLabel(feedback.category)}
                                                </span>
                                                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    • {formatDate(feedback.createdAt)}
                                                </span>
                                            </div>
                                        </div>
                                        {feedback.attachmentUrls?.length > 0 && (
                                            <span className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                                📎 {feedback.attachmentUrls.length}
                                            </span>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Feedback Detail */}
                <div className={`w-96 rounded-lg shadow overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                    {selectedFeedback ? (
                        <div className="h-full flex flex-col">
                            {/* Detail Header */}
                            <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                <div className="flex items-center justify-between">
                                    <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Feedback Details</h3>
                                    <button
                                        onClick={() => handleDelete(selectedFeedback.id)}
                                        className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                        title="Delete feedback"
                                    >
                                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Detail Content */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {/* User Info */}
                                <div>
                                    <p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>FROM</p>
                                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedFeedback.userName}</p>
                                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{selectedFeedback.userEmail}</p>
                                </div>

                                {/* Rating & Category */}
                                <div className="flex gap-4">
                                    <div>
                                        <p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>RATING</p>
                                        <p className="text-2xl">{getRatingEmoji(selectedFeedback.rating)}</p>
                                    </div>
                                    <div>
                                        <p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>CATEGORY</p>
                                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                            {getCategoryLabel(selectedFeedback.category)}
                                        </p>
                                    </div>
                                </div>

                                {/* Status */}
                                <div>
                                    <p className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>STATUS</p>
                                    <div className="flex gap-2">
                                        {(['new', 'read', 'resolved'] as FeedbackStatus[]).map(status => (
                                            <button
                                                key={status}
                                                onClick={() => handleStatusChange(selectedFeedback.id, status)}
                                                className={`px-3 py-1 text-xs rounded-full transition-colors ${selectedFeedback.status === status
                                                    ? status === 'new' ? 'bg-blue-500 text-white' :
                                                        status === 'read' ? 'bg-yellow-500 text-white' :
                                                            'bg-green-500 text-white'
                                                    : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {status.charAt(0).toUpperCase() + status.slice(1)}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Message */}
                                <div>
                                    <p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>MESSAGE</p>
                                    <p className={`mt-1 text-sm whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {selectedFeedback.message}
                                    </p>
                                </div>

                                {/* Attachments */}
                                {selectedFeedback.attachmentUrls?.length > 0 && (
                                    <div>
                                        <p className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            ATTACHMENTS ({selectedFeedback.attachmentUrls.length})
                                        </p>
                                        <div className="grid grid-cols-2 gap-2">
                                            {selectedFeedback.attachmentUrls.map((url, index) => (
                                                <a
                                                    key={index}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block"
                                                >
                                                    <img
                                                        src={url}
                                                        alt={`Attachment ${index + 1}`}
                                                        className="w-full h-24 object-cover rounded-lg border border-gray-200 dark:border-gray-700 hover:opacity-80 transition-opacity"
                                                    />
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Page URL */}
                                <div>
                                    <p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>PAGE</p>
                                    <a
                                        href={selectedFeedback.pageUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-cyan-500 hover:underline truncate block"
                                    >
                                        {selectedFeedback.pageUrl}
                                    </a>
                                </div>

                                {/* Date */}
                                <div>
                                    <p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>SUBMITTED</p>
                                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {formatDate(selectedFeedback.createdAt)}
                                    </p>
                                </div>

                                {/* Admin Response */}
                                {selectedFeedback.adminResponse && (
                                    <div className={`p-3 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
                                        <p className={`text-xs font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                                            ADMIN RESPONSE
                                        </p>
                                        <p className={`mt-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                            {selectedFeedback.adminResponse}
                                        </p>
                                        <p className={`mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                            By {selectedFeedback.respondedBy} • {selectedFeedback.respondedAt && formatDate(selectedFeedback.respondedAt)}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Response Input */}
                            {!selectedFeedback.adminResponse && (
                                <div className={`p-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <textarea
                                        value={responseText}
                                        onChange={(e) => setResponseText(e.target.value)}
                                        placeholder="Type your response..."
                                        rows={3}
                                        className={`w-full px-3 py-2 rounded-lg border text-sm resize-none ${isDark
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                            } focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                                    />
                                    <button
                                        onClick={handleRespond}
                                        disabled={!responseText.trim() || isResponding}
                                        className={`mt-2 w-full py-2 rounded-lg font-medium text-sm transition-colors ${responseText.trim() && !isResponding
                                            ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                                            : 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-700'
                                            }`}
                                    >
                                        {isResponding ? 'Sending...' : 'Send Response'}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="h-full flex items-center justify-center p-8">
                            <div className="text-center">
                                <svg className={`mx-auto h-12 w-12 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                </svg>
                                <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Select feedback to view details</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminFeedbackManager;
